import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { marketplaceApi } from '../services/api';
import type { Lawyer } from '../types';

interface ConnectedLawyer {
  lawyer: Lawyer;
  projects: Array<{
    projectId: string;
    projectNumber: number;
    description: string;
    invitedAt: string;
  }>;
}

function normalizeConnectedLawyer(item: ConnectedLawyer | Lawyer): ConnectedLawyer {
  if ('lawyer' in item) {
    return {
      lawyer: item.lawyer,
      projects: Array.isArray(item.projects) ? item.projects : [],
    };
  }

  return {
    lawyer: item,
    projects: [],
  };
}

export function MyLawyers() {
  const { t } = useTranslation();
  const [connectedLawyers, setConnectedLawyers] = useState<ConnectedLawyer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedPresentation, setExpandedPresentation] = useState<string | null>(null);

  useEffect(() => {
    marketplaceApi.getMyConnectedLawyers()
      .then(data => setConnectedLawyers(data.map(normalizeConnectedLawyer)))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('myLawyers.title', 'Jurister')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: t('nav.contacts', 'Kontakter') },
            { label: t('myLawyers.title', 'Jurister') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('myLawyers.description', 'Jurister kopplade till dina projekt')}</p>
        </div>
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      ) : connectedLawyers.length === 0 ? (
        <Card>
          <div className="card-body text-center py-5">
            <i className="feather-briefcase text-muted mb-3" style={{ fontSize: 48 }}></i>
            <h6 className="text-muted mb-2">{t('myLawyers.noLawyers', 'Inga jurister kopplade ännu')}</h6>
            <p className="text-muted small mb-0">{t('myLawyers.noLawyersHint', 'Jurister visas här när de har bjudits in till ett av dina projekt via marknadsplatsen.')}</p>
          </div>
        </Card>
      ) : (
        <div className="d-flex flex-column gap-4">
          {connectedLawyers.map(({ lawyer, projects }) => {
            const connectedProjects = Array.isArray(projects) ? projects : [];
            return (
            <div key={lawyer.id} className="card">
              <div className="card-body">
                {/* Connection context */}
                <div className="alert alert-light border py-2 px-3 mb-3 d-flex align-items-start gap-2">
                  <i className="feather-link text-primary mt-1" style={{ fontSize: 14 }}></i>
                  <div>
                    <small className="fw-semibold d-block">{t('myLawyers.connectionReason', 'Kopplad via projektinbjudan')}</small>
                    <small className="text-muted">
                      {connectedProjects.length === 0
                        ? t('myLawyers.marketplaceConnection', 'Kopplad via marknadsplatsen')
                        : connectedProjects.length === 1
                          ? t('myLawyers.singleProject', 'Inbjuden till projekt #{{num}}', { num: connectedProjects[0].projectNumber })
                          : t('myLawyers.multipleProjects', 'Inbjuden till {{count}} projekt', { count: connectedProjects.length })
                      }
                    </small>
                  </div>
                </div>

                {/* Header */}
                <div className="d-flex align-items-start gap-3 mb-3">
                  <div className="avatar-text rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 56, height: 56, fontSize: 18, fontWeight: 600 }}>
                    {getInitials(lawyer.name)}
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                      <span className="fw-bold fs-6">{lawyer.name}</span>
                      {lawyer.title && <span className="text-muted">— {lawyer.title}</span>}
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      {lawyer.verifiedLawyer === 'Verifierad' && (
                        <span className="badge bg-success"><i className="feather-check me-1" style={{ fontSize: 10 }}></i>{t('marketplace.provider.verified')}</span>
                      )}
                      <span className={`badge ${lawyer.availability ? 'bg-success' : 'bg-secondary'}`}>
                        <i className={`feather-${lawyer.availability ? 'check-circle' : 'x-circle'} me-1`} style={{ fontSize: 10 }}></i>
                        {lawyer.availability ? t('marketplace.provider.availableForWork') : t('marketplace.provider.notAvailable')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Contact info */}
                {(lawyer.phone || lawyer.email || lawyer.website) && (
                  <div className="d-flex flex-wrap gap-3 mb-3 small">
                    {lawyer.phone && (
                      <span className="text-muted"><i className="feather-phone me-1" style={{ fontSize: 13 }}></i>{lawyer.phone}</span>
                    )}
                    {lawyer.email && (
                      <span className="text-muted"><i className="feather-mail me-1" style={{ fontSize: 13 }}></i>{lawyer.email}</span>
                    )}
                    {lawyer.website && (
                      <span className="text-muted"><i className="feather-globe me-1" style={{ fontSize: 13 }}></i>{lawyer.website}</span>
                    )}
                  </div>
                )}

                {/* Specialties */}
                {(lawyer.specialties || []).length > 0 && (
                  <div className="mb-3">
                    <small className="fw-semibold text-muted d-block mb-1">{t('marketplace.provider.specialties')}</small>
                    <div className="d-flex flex-wrap gap-1">
                      {(lawyer.specialties || []).map((s: string, i: number) => (
                        <span key={i} className="badge bg-light text-dark border">{s}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Practical info grid */}
                <div className="bg-light rounded p-3 mb-3">
                  <small className="fw-semibold text-muted d-block mb-2">{t('marketplace.provider.practicalInfo')}</small>
                  <div className="row g-2">
                    {lawyer.hourlyRate && (
                      <div className="col-6 col-md-4">
                        <div className="d-flex align-items-center gap-2">
                          <i className="feather-tag text-muted" style={{ fontSize: 14 }}></i>
                          <div>
                            <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.hourlyRate')}</div>
                            <div className="fw-semibold small">{lawyer.hourlyRate}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {lawyer.fixedPriceAvailable && (
                      <div className="col-6 col-md-4">
                        <div className="d-flex align-items-center gap-2">
                          <i className="feather-tag text-muted" style={{ fontSize: 14 }}></i>
                          <div>
                            <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.fixedPrice')}</div>
                            <div className="fw-semibold small text-success">{t('common.yes', 'Ja')}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {lawyer.responseTime && (
                      <div className="col-6 col-md-4">
                        <div className="d-flex align-items-center gap-2">
                          <i className="feather-clock text-muted" style={{ fontSize: 14 }}></i>
                          <div>
                            <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.responseTime')}</div>
                            <div className="fw-semibold small">{lawyer.responseTime}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {lawyer.experienceYears > 0 && (
                      <div className="col-6 col-md-4">
                        <div className="d-flex align-items-center gap-2">
                          <i className="feather-award text-muted" style={{ fontSize: 14 }}></i>
                          <div>
                            <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.experience')}</div>
                            <div className="fw-semibold small">{lawyer.experienceYears} år</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {lawyer.geographicArea && (
                      <div className="col-6 col-md-4">
                        <div className="d-flex align-items-center gap-2">
                          <i className="feather-map-pin text-muted" style={{ fontSize: 14 }}></i>
                          <div>
                            <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.geographicArea')}</div>
                            <div className="fw-semibold small">{lawyer.geographicArea}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {(lawyer.languages || []).length > 0 && (
                      <div className="col-6 col-md-4">
                        <div className="d-flex align-items-center gap-2">
                          <i className="feather-globe text-muted" style={{ fontSize: 14 }}></i>
                          <div>
                            <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.languages')}</div>
                            <div className="fw-semibold small">{(lawyer.languages || []).join(', ')}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {lawyer.initialMeetingFree && (
                      <div className="col-6 col-md-4">
                        <div className="d-flex align-items-center gap-2">
                          <i className="feather-coffee text-muted" style={{ fontSize: 14 }}></i>
                          <div>
                            <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.initialMeeting')}</div>
                            <div className="fw-semibold small text-success">{t('marketplace.provider.initialMeetingFree')}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {lawyer.leadTime && (
                      <div className="col-6 col-md-4">
                        <div className="d-flex align-items-center gap-2">
                          <i className="feather-calendar text-muted" style={{ fontSize: 14 }}></i>
                          <div>
                            <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.leadTime')}</div>
                            <div className="fw-semibold small">{lawyer.leadTime}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Connected projects list */}
                {connectedProjects.length > 0 && (
                  <div className="mb-3">
                    <small className="fw-semibold text-muted d-block mb-2">{t('myLawyers.connectedProjects', 'Kopplade projekt')}</small>
                    <div className="d-flex flex-wrap gap-2">
                      {connectedProjects.map(p => (
                        <Link key={p.projectId} to={`/person/projects/${p.projectId}`} className="badge bg-light text-dark border text-decoration-none">
                          <i className="feather-folder me-1" style={{ fontSize: 10 }}></i>
                          #{p.projectNumber}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show/hide presentation */}
                {lawyer.lawyerPresentation && (
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setExpandedPresentation(expandedPresentation === lawyer.id ? null : lawyer.id)}>
                    <i className={`feather-${expandedPresentation === lawyer.id ? 'chevron-up' : 'chevron-down'} me-1`}></i>
                    {expandedPresentation === lawyer.id ? t('marketplace.provider.hidePresentation') : t('marketplace.provider.showPresentation')}
                  </button>
                )}

                {expandedPresentation === lawyer.id && lawyer.lawyerPresentation && (
                  <div className="mt-3 p-3 border rounded bg-white">
                    <small className="fw-semibold text-muted d-block mb-2">{t('marketplace.provider.aboutMe')}</small>
                    <div className="ql-editor p-0" dangerouslySetInnerHTML={{ __html: lawyer.lawyerPresentation }} />
                  </div>
                )}
              </div>
            </div>
          )})}
        </div>
      )}
    </DashboardLayout>
  );
}
